import * as CL from '@design-system/component-library';
import { useState } from 'react';
import { dsClass } from '../../common/constants/dsClasses.js';
import { t } from '../../common/i18n/index.js';
import { eurosToCents } from '../../common/utils/priceUtils.js';
import { getLocalizedString, isMandatoryAddon } from '../../common/utils/productOfferingsUtils.js';
import { DialogWrapper } from '../DialogWrapper/index.js';
import { pictureStorageBaseUrl } from '../Picture/contentfulImageProxy.js';
import { PlanProductRuntimeCharacteristic } from '../PlaceOrder/utils/characteristicsFlagsUtilsCoCo.js';
import {
  buildCharacteristicSelection,
  getDefaultCharacteristicValue,
} from '../PlaceOrder/utils/runtimeCharacteristicsCoCo.js';
import { BundleAddonGroupSelectorCoCo } from '../ProductOfferingAddonCoCo/BundleAddonGroupSelectorCoCo.js';
import { CommercialAddonsSectionCoCo } from '../ProductOfferingAddonCoCo/CommercialAddonsSectionCoCo.js';
import { ProductOfferAddOn } from '../ProductOfferingAddonCoCo/ProductOfferAddOnCoCo.js';
import {
  calculateTotalCheckedPrice,
  createCartItemFromProductOffering,
  getDefaultOfferingPriceInEuros,
  getProductOfferAddOnPrices,
  getRecurringOfferingPriceInEuros,
  validateGroups,
} from './ProductOfferingsModalCoCoUtils.js';
import { ProductOfferingsModalFooterCoCo } from './ProductOfferingsModalFooterCoCo.js';
import type { ProductOrderProps } from '../../common/types/shoppingBasketCoCo.js';
import type { ProductOfferingCoco } from '../../generated/api/productOfferingCoco.js';
import type { ProductOfferingPrice } from '../../generated/api/productOfferingPrice.js';
import type { ProductOfferingsResponse } from '../../generated/api/productOfferingsResponse.js';
import type { BundledAddonGroupSelection } from '../ProductOfferingAddonCoCo/BundleAddonGroupSelectorCoCo.js';
import type { ProductOfferAddOnProps } from '../ProductOfferingAddonCoCo/ProductOfferAddOnCoCo.js';
import type { JSX } from 'react';

import '../HorizontalSubscriptionCard/SubscriptionModal.scss';

export interface ProductOfferingWithSelection extends ProductOfferingCoco {
  isChecked: boolean;
}

export interface ModalDescriptionItem {
  title?: string;
  content?: JSX.Element;
}

export interface ProductOfferingsModalCoCoContentProps {
  header: string;
  descriptionItems: ModalDescriptionItem[];
  productOfferings: ProductOfferingsResponse;
  bundledOfferings: ProductOfferingWithSelection[];
  commercialAddonOfferings: ProductOfferingWithSelection[];
  bundleAddonGroups: NonNullable<ProductOfferingsResponse['bundleAddonGroup']>;
  groupSelections: BundledAddonGroupSelection[];
  groupValidationErrors: Record<string, string>;
  selectedRuntimeCharacteristicValue?: string;
  onRuntimeCharacteristicValueChange: (value: string) => void;
  onAddOnChange: (id: string, checked: boolean) => void;
  onCommercialAddonChange: (id: string, checked: boolean) => void;
  onGroupSelectionChange: (groupId: string, selectedIds: string[]) => void;
  convertToProductOfferingAddOn: (
    offering: ProductOfferingWithSelection,
    onChange: (id: string, checked: boolean) => void
  ) => ProductOfferAddOnProps;
}

export interface ProductOfferingsModalCoCoConfig {
  getAddOnPriceInEuros?: (productOfferingPrice: ProductOfferingPrice[] | undefined) => number;
  initializeGroupSelection?: (
    group: NonNullable<ProductOfferingsResponse['bundleAddonGroup']>[number]
  ) => BundledAddonGroupSelection;
  calculatePrices?: (context: {
    bundledOfferings: ProductOfferingWithSelection[];
    commercialAddonOfferings: ProductOfferingWithSelection[];
    bundleAddonGroups: NonNullable<ProductOfferingsResponse['bundleAddonGroup']>;
    groupSelections: BundledAddonGroupSelection[];
    recurringCharge: number;
    oneTimeCharge: number;
    quantity: number;
  }) => { recurringPriceInCents: number; oneTimePriceInCents: number };
  renderContent?: (props: ProductOfferingsModalCoCoContentProps) => JSX.Element;
}

export interface ProductOfferingsModalCoCoProps {
  header: string;
  image?: string;
  descriptionItems: ModalDescriptionItem[];
  onCloseDialog: () => void;
  recurringCharge: number;
  oneTimeCharge: number;
  productOfferings: ProductOfferingsResponse;
  publicPages?: boolean;
  hasQuantity?: boolean;
  isChangeOffer?: boolean;
  commercialAddonProductOfferings?: ProductOfferingCoco[];
  config?: ProductOfferingsModalCoCoConfig;
  onOrder: ({ selectedProductOffers }: ProductOrderProps) => void;
}

export const ProductOfferingsModalCoCo = ({
  header,
  descriptionItems,
  onCloseDialog,
  hasQuantity,
  recurringCharge,
  oneTimeCharge,
  productOfferings,
  image,
  commercialAddonProductOfferings = [],
  config,
  onOrder,
}: ProductOfferingsModalCoCoProps) => {
  const [quantity, setQuantity] = useState<number>(1);
  const runTimeCharacteristics = productOfferings.planProductOffering?.productOfferingRuntimeCharacteristic ?? [];
  const [selectedRuntimeCharacteristicValue, setSelectedRuntimeCharacteristicValue] = useState<string | undefined>(() =>
    getDefaultCharacteristicValue(runTimeCharacteristics, PlanProductRuntimeCharacteristic.FIBER_MODE)
  );

  const bundleAddonProductOffering = productOfferings.bundleAddonProductOffering ?? [];
  const [bundledOfferings, setBundledOfferings] = useState<ProductOfferingWithSelection[]>(() =>
    bundleAddonProductOffering.map(offering => ({
      ...offering,
      isChecked: (offering.cardinality?.defaultNum ?? 0) >= 1,
    }))
  );

  const bundleAddonGroups = productOfferings.bundleAddonGroup ?? [];
  const [groupSelections, setGroupSelections] = useState<BundledAddonGroupSelection[]>(() =>
    bundleAddonGroups.map(group => {
      if (config?.initializeGroupSelection) {
        return config.initializeGroupSelection(group);
      }
      const isSingleSelect = group.cardinality?.maxNum === 1;
      const offerings = group.productOffering ?? [];

      if (isSingleSelect) {
        const defaultOffering = offerings.find(o => o.isDefault === true);
        const fallbackOfferingIds = offerings.length > 0 ? [offerings[0].id] : [];
        return {
          groupId: group.id,
          selectedOfferingIds: defaultOffering ? [defaultOffering.id] : fallbackOfferingIds,
        };
      }

      // Multi-select: pre-select offerings marked as default
      return {
        groupId: group.id,
        selectedOfferingIds: offerings.filter(o => o.isDefault === true).map(o => o.id),
      };
    })
  );

  const [commercialAddonOfferings, setCommercialAddonOfferings] = useState<ProductOfferingWithSelection[]>(() =>
    commercialAddonProductOfferings.map(offering => ({
      ...offering,
      isChecked: (offering.cardinality?.defaultNum ?? 0) >= 1,
    }))
  );

  const totalAddOnPrice = calculateTotalCheckedPrice(bundledOfferings);

  const totalAddOnGroupPrice = groupSelections.reduce((total, selection) => {
    const group = bundleAddonGroups.find(g => g.id === selection.groupId);
    if (!group) {
      return total;
    }
    return (
      total +
      selection.selectedOfferingIds.reduce((groupTotal, offeringId) => {
        const offering = group.productOffering?.find(o => o.id === offeringId);
        return groupTotal + eurosToCents(getRecurringOfferingPriceInEuros(offering?.productOfferingPrice));
      }, 0)
    );
  }, 0);

  const totalCommercialAddonPrice = calculateTotalCheckedPrice(commercialAddonOfferings);

  const prices = config?.calculatePrices?.({
    bundledOfferings,
    commercialAddonOfferings,
    bundleAddonGroups,
    groupSelections,
    recurringCharge,
    oneTimeCharge,
    quantity,
  }) ?? {
    recurringPriceInCents:
      (totalAddOnPrice + totalAddOnGroupPrice + totalCommercialAddonPrice + recurringCharge) * quantity,
    oneTimePriceInCents: oneTimeCharge,
  };

  const handleAddOnChange = (id: string, checked: boolean) => {
    setBundledOfferings(prev =>
      prev.map(offering => (offering.id === id ? { ...offering, isChecked: checked } : offering))
    );
  };

  const handleCommercialAddonChange = (id: string, checked: boolean) => {
    setCommercialAddonOfferings(prev =>
      prev.map(offering => (offering.id === id ? { ...offering, isChecked: checked } : offering))
    );
  };

  const [groupValidationErrors, setGroupValidationErrors] = useState<Record<string, string>>({});

  const handleGroupSelectionChange = (groupId: string, selectedIds: string[]) => {
    setGroupSelections(prev =>
      prev.map(selection =>
        selection.groupId === groupId ? { ...selection, selectedOfferingIds: selectedIds } : selection
      )
    );
    setGroupValidationErrors(prev => {
      const { [groupId]: _, ...rest } = prev;
      return rest;
    });
  };

  const selectedProductOffers = createCartItemFromProductOffering(
    productOfferings,
    quantity,
    bundledOfferings,
    groupSelections,
    commercialAddonOfferings,
    buildCharacteristicSelection(
      runTimeCharacteristics,
      PlanProductRuntimeCharacteristic.FIBER_MODE,
      selectedRuntimeCharacteristicValue
    )
  );

  const handleOrder = () => {
    const errors = validateGroups(bundleAddonGroups, groupSelections);
    setGroupValidationErrors(errors);
    if (Object.keys(errors).length === 0) {
      onCloseDialog();
      onOrder({ selectedProductOffers, quantity });
    }
  };

  const convertToProductOfferingAddOn = (
    bundledOffering: ProductOfferingWithSelection,
    onChange: (id: string, checked: boolean) => void
  ) => ({
    linkInfo: {
      id: bundledOffering.id,
      linkInfoDescription: bundledOffering.description ? getLocalizedString(bundledOffering.description) : '',
    },
    initialValue: bundledOffering.isChecked,
    id: bundledOffering.id,
    handleAddOnChange: onChange,
    priceInfo: getProductOfferAddOnPrices(
      config?.getAddOnPriceInEuros?.(bundledOffering.productOfferingPrice) ??
        getDefaultOfferingPriceInEuros(bundledOffering.productOfferingPrice)
    ),
    title: getLocalizedString(bundledOffering.name),
    disabled: isMandatoryAddon(bundledOffering),
  });

  const contentProps: ProductOfferingsModalCoCoContentProps = {
    header,
    descriptionItems,
    productOfferings,
    bundledOfferings,
    commercialAddonOfferings,
    bundleAddonGroups,
    groupSelections,
    groupValidationErrors,
    selectedRuntimeCharacteristicValue,
    onRuntimeCharacteristicValueChange: setSelectedRuntimeCharacteristicValue,
    onAddOnChange: handleAddOnChange,
    onCommercialAddonChange: handleCommercialAddonChange,
    onGroupSelectionChange: handleGroupSelectionChange,
    convertToProductOfferingAddOn,
  };

  return (
    <DialogWrapper size="l" closeable={true} onCloseDialog={onCloseDialog} header={header}>
      <div className="of-subscription-modal of-subscription-modal--scrollable">
        <div className="of-subscription-modal-main">
          <div className="of-subscription-modal-body">
            {config?.renderContent ? (
              config.renderContent(contentProps)
            ) : image ? (
              <div className="of-subscription-modal-software-header">
                <div className="of-subscription-modal-software-image">
                  <img alt="" src={`${pictureStorageBaseUrl}${image}`} />
                </div>
                <div className="of-subscription-modal-software-description">
                  <h3 className={dsClass.MARGIN_TOP_0}>{header}</h3>
                  {descriptionItems.map((item, index) => (
                    <div key={item.title ?? index}>{item.content}</div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="of-subscription-modal-details-description">
                <CL.Description
                  listTitle={t.NUCX('Subscription contents')}
                  items={descriptionItems.map(item => ({
                    title: item.title,
                    description: item.content,
                  }))}
                />
              </div>
            )}
            {!config?.renderContent && (
              <div>
                <div className={`${dsClass.FONT_WEIGHT_700} ${dsClass.MARGIN_BOTTOM_2} ${dsClass.MARGIN_TOP_3}`}>
                  {t.UU5G('Select additional services')}
                </div>
                {bundledOfferings.map(bundledOffering => (
                  <div className={`${dsClass.MARGIN_TOP_2}`} key={bundledOffering.id}>
                    <ProductOfferAddOn {...convertToProductOfferingAddOn(bundledOffering, handleAddOnChange)} />
                  </div>
                ))}
                {bundleAddonGroups.map(group => {
                  const selection = groupSelections.find(s => s.groupId === group.id);
                  return (
                    <BundleAddonGroupSelectorCoCo
                      key={group.id}
                      group={group}
                      selectedOfferingIds={selection?.selectedOfferingIds ?? []}
                      onSelectionChange={handleGroupSelectionChange}
                      errorMessage={groupValidationErrors[group.id]}
                    />
                  );
                })}
                {commercialAddonOfferings.length > 0 && (
                  <CommercialAddonsSectionCoCo
                    addons={commercialAddonOfferings}
                    onAddOnChange={handleCommercialAddonChange}
                    getPriceInEuros={offering =>
                      config?.getAddOnPriceInEuros?.(offering.productOfferingPrice) ??
                      getDefaultOfferingPriceInEuros(offering.productOfferingPrice)
                    }
                  />
                )}
              </div>
            )}
          </div>
        </div>
        <ProductOfferingsModalFooterCoCo
          selectedProductOffers={selectedProductOffers}
          totalPriceInCents={prices.recurringPriceInCents}
          oneTimeChargeInCents={prices.oneTimePriceInCents}
          hasQuantity={hasQuantity}
          quantity={quantity}
          onQuantityChange={setQuantity}
          onCloseDialog={onCloseDialog}
          onOrder={onOrder}
          onAddToBasket={handleOrder}
        />
      </div>
    </DialogWrapper>
  );
};
